import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import isEqual from 'lodash-es/isEqual';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useTitle from 'react-use/lib/useTitle';

import DynamicBreadcrumbs from 'src/common/components/Breadcrumbs';
import LightTooltip from 'src/common/components/styled/LightTooltip';
import { dashboardPageRootSx } from 'src/common/components/dashboardPageLayout';
import DisplayHeadingWithRule from 'src/common/components/DisplayHeadingWithRule';
import DashboardCollapsibleHeaderActions from 'src/common/components/dashboard/DashboardCollapsibleHeaderActions';
import {
  dashboardPageHeaderSx,
  dashboardPageHeaderTitleSlotSx,
  dashboardPageHeaderTitleTypographySx,
} from 'src/common/components/DashboardPageToolbar';
import IconPickerField from 'src/common/components/form/IconPickerField';
import PageBox from 'src/common/components/PageBox';
import { LanguageKeys } from 'src/common/constants/languages';
import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import {
  buildPageEditorSnapshot,
  isPageEditorDirty,
  PageEditorDirtySnapshot,
} from 'src/helpers/pageEditorDirtyUtils';
import { normalizePageDocument } from 'src/helpers/pageDocumentUtils';
import usePageBlocksContent from 'src/hooks/usePageBlocksContent';
import useUnsavedChangesGuard from 'src/hooks/useUnsavedChangesGuard';
import LanguageFlagSwitcher from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import MenuLocationsSelect from 'src/pages/Dashboard/System/Pages/components/MenuLocationsSelect';
import PageEditorActionFab from 'src/pages/Dashboard/System/Pages/components/PageEditorActionFab';
import HomeHeroSectionEditor from 'src/pages/Dashboard/System/Pages/components/HomeHeroSectionEditor';
import SignupPageBlocksPanel from 'src/pages/Dashboard/System/Pages/components/SignupPageBlocksPanel';
import PageDocumentEditor from 'src/pages/Dashboard/System/Pages/components/PageDocumentEditor';
import { PublicPageShell } from 'src/interfaces/PublicPage';
import useTrackMapsStore from 'src/stores/trackMapsStore';
import {
  getGroupRedirectChildPages,
  getParentPublicPage,
  getPublicPageMenuLabel,
  nextMenuSortOrder,
  normalizePublicPageRefId,
  pathSegmentsFromPage,
  resolveFullPathsFromSegments,
  resolveRedirectToPageId,
  slugifyLabel,
} from 'src/helpers/publicPageUtils';
import {
  hasMenuLocation,
  normalizeMenuLocations,
  PublicPageMenuLocation,
} from 'src/helpers/publicPageMenuLocations';
import {
  labelFieldError,
  pathFieldError,
  PublicPageEditorErrors,
  PublicPageEditorField,
  validatePublicPageEditor,
} from 'src/helpers/validatePublicPageEditor';
import { IBasePublicPage, IPublicPage } from 'src/interfaces/PublicPage';
import usePublicPagesStore from 'src/stores/publicPagesStore';

const emptyPage = (): IBasePublicPage => ({
  kind: 'page',
  key: '',
  contentKey: '',
  sections: ['main'],
  paths: { en: '', hu: '', ro: '' },
  menu: {
    menuLocations: ['top'],
    parentId: null,
    sortOrder: 0,
    icon: 'info',
    labels: { en: '', hu: '', ro: '' },
  },
  published: false,
});

import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { ticketCardGridContainerPaddingSx } from 'src/common/styles/ticketCardStyles';

const formPaperSx = {
  ...frostedPanelSx(0.5),
  p: { xs: 2, sm: 3 },
  mb: 2,
  mt: 2,
};

export default function PageEditor() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = id === 'new';

  const { publicPages, publicPagesById, publicPagesByKey, savePublicPage, isLoading } =
    usePublicPagesStore(s => s);

  const existing = !isNew && id ? publicPagesById?.[id] : undefined;
  const [form, setForm] = useState<IBasePublicPage>(existing ?? emptyPage());
  const [activeLang, setActiveLang] = useState<LanguageKeys>(LanguageKeys.en);
  const [pathSegments, setPathSegments] = useState<Record<LanguageKeys, string>>({
    en: '',
    hu: '',
    ro: '',
  });
  const [saving, setSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<PublicPageEditorErrors>({});
  const [inlineActionsVisible, setInlineActionsVisible] = useState(true);
  const inlineActionsRef = useRef<HTMLDivElement>(null);
  const [savedBaseline, setSavedBaseline] = useState<PageEditorDirtySnapshot | null>(null);

  useEffect(() => {
    if (existing) {
      setForm({
        ...existing,
        redirectTo:
          resolveRedirectToPageId(existing.redirectTo, publicPages, publicPagesByKey) ??
          normalizePublicPageRefId(existing.redirectTo),
        menu: {
          ...existing.menu,
          menuLocations: normalizeMenuLocations(existing.menu),
          parentId: normalizePublicPageRefId(existing.menu.parentId) ?? null,
        },
      });
      setPathSegments(pathSegmentsFromPage(existing, publicPagesById));
    }
  }, [id, existing, publicPages, publicPagesById, publicPagesByKey]);

  const effectiveContentKey = (form.contentKey || form.key || '').trim();
  const blocksEditor = usePageBlocksContent(effectiveContentKey);

  useEffect(() => {
    const nextBaseline = isNew
      ? buildPageEditorSnapshot(
          emptyPage(),
          { en: '', hu: '', ro: '' },
          { blocks: [] },
          publicPagesById,
        )
      : existing
        ? buildPageEditorSnapshot(
            {
              ...existing,
              redirectTo:
                resolveRedirectToPageId(existing.redirectTo, publicPages, publicPagesByKey) ??
                normalizePublicPageRefId(existing.redirectTo),
              menu: {
                ...existing.menu,
                menuLocations: normalizeMenuLocations(existing.menu),
                parentId: normalizePublicPageRefId(existing.menu.parentId) ?? null,
              },
            },
            pathSegmentsFromPage(existing, publicPagesById),
            normalizePageDocument(blocksEditor.pageContent),
            publicPagesById,
          )
        : null;

    if (!nextBaseline) return;

    setSavedBaseline(prev => (prev && isEqual(prev, nextBaseline) ? prev : nextBaseline));
    // Re-sync when persisted identity changes so in-progress local edits are not wiped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isNew,
    id,
    existing,
    publicPages,
    publicPagesById,
    publicPagesByKey,
    blocksEditor.pageContent?.id,
    blocksEditor.pageContent?.updatedAt,
  ]);

  const currentSnapshot = useMemo(
    () =>
      buildPageEditorSnapshot(form, pathSegments, blocksEditor.document, publicPagesById),
    [form, pathSegments, blocksEditor.document, publicPagesById],
  );

  const isDirty = isPageEditorDirty(currentSnapshot, savedBaseline);

  const { confirmLeave, allowNavigation } = useUnsavedChangesGuard(isDirty, {
    title: t('system.pages.unsavedChangesTitle'),
    description: t('system.pages.unsavedChangesDescription'),
    confirmationText: t('system.pages.unsavedChangesConfirm'),
    cancellationText: t('system.pages.unsavedChangesCancel'),
  });

  useEffect(() => {
    const el = inlineActionsRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setInlineActionsVisible(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pageDisplayTitle = isNew
    ? t('system.pages.addPage')
    : getPublicPageMenuLabel(form, activeLang);

  useTitle(`${pageDisplayTitle} - ${t('system.pages.pageTitle')} - ${t('base.defaultTitle')}`);

  const isPageKind = form.kind === 'page';
  const isHomeShell = form.shell === 'home';
  const isSignupShell = form.shell === 'signup';
  const trackMaps = useTrackMapsStore(s => s.trackMaps);
  const groups = publicPages.filter(p => p.kind === 'group');
  const redirectChildPages = getGroupRedirectChildPages(
    publicPages,
    existing ?? (form.id ? form : undefined),
    form.redirectTo,
    publicPagesByKey,
  );
  const redirectToValue =
    resolveRedirectToPageId(form.redirectTo, publicPages, publicPagesByKey) ?? '';

  const clearFieldError = (field: PublicPageEditorField) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const clearLabelErrors = () => {
    setFieldErrors(prev => {
      const hasLabelError = (['en', 'hu', 'ro'] as LanguageKeys[]).some(l => prev[`labels.${l}`]);
      if (!hasLabelError) return prev;
      const next = { ...prev };
      (['en', 'hu', 'ro'] as LanguageKeys[]).forEach(l => delete next[`labels.${l}`]);
      return next;
    });
  };

  const clearPathErrors = () => {
    setFieldErrors(prev => {
      const hasPathError = (['en', 'hu', 'ro'] as LanguageKeys[]).some(l => prev[`paths.${l}`]);
      if (!hasPathError) return prev;
      const next = { ...prev };
      (['en', 'hu', 'ro'] as LanguageKeys[]).forEach(l => delete next[`paths.${l}`]);
      return next;
    });
  };

  const update = (patch: Partial<IBasePublicPage>) => {
    setForm(prev => ({ ...prev, ...patch }));
    if ('key' in patch) clearFieldError('key');
    if ('redirectTo' in patch) clearFieldError('redirectTo');
    if (patch.menu?.icon != null) clearFieldError('menu.icon');
  };

  const updatePathSegment = (lang: LanguageKeys, value: string) => {
    setPathSegments(prev => ({ ...prev, [lang]: value }));
    clearPathErrors();
  };

  const parentPage = getParentPublicPage(form, publicPagesById);

  const updateLabel = (lang: LanguageKeys, value: string) => {
    setForm(prev => ({
      ...prev,
      menu: { ...prev.menu, labels: { ...prev.menu.labels, [lang]: value } },
    }));
    clearLabelErrors();
  };

  const updateMenuLocations = (menuLocations: PublicPageMenuLocation[]) => {
    setForm(prev => {
      const menu = { ...prev.menu, menuLocations };
      if (!menuLocations.includes('top')) {
        menu.parentId = null;
      }
      return { ...prev, menu };
    });
  };

  const showTopMenuFields = hasMenuLocation(form.menu, 'top');

  const handleSlugify = (lang: LanguageKeys) => {
    const label = form.menu.labels[lang];
    if (!label) return;
    updatePathSegment(lang, slugifyLabel(label));
  };

  const handleCancel = async () => {
    if (await confirmLeave()) {
      navigate('/dashboard/system/pages');
    }
  };

  const handleSave = async () => {
    if (isSavingRef.current) {
      return;
    }
    const errors = validatePublicPageEditor(form, pathSegments, publicPagesById, t);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      enqueueSnackbar(t('system.pages.validation.fixErrors'), { variant: 'warning' });
      return;
    }
    setFieldErrors({});

    isSavingRef.current = true;
    setSaving(true);
    try {
      const payload: IBasePublicPage = {
        ...form,
        paths: resolveFullPathsFromSegments(pathSegments, form, publicPagesById),
        menu: {
          ...form.menu,
          menuLocations: normalizeMenuLocations(form.menu),
        },
      };
      if (payload.kind === 'page') {
        payload.contentKey = payload.contentKey || payload.key;
        payload.redirectTo = undefined;
      }
      if (isNew) {
        payload.menu = {
          ...payload.menu,
          sortOrder: nextMenuSortOrder(publicPages, payload.menu.parentId),
        };
      }
      const saved = await savePublicPage(payload, isNew ? undefined : id);
      if (payload.kind === 'page') {
        const contentKeyToSave = payload.contentKey || payload.key;
        if (contentKeyToSave) {
          await blocksEditor.save();
        }
      }
      setSavedBaseline(
        buildPageEditorSnapshot(form, pathSegments, blocksEditor.document, publicPagesById),
      );
      enqueueSnackbar(t('system.pages.saveSuccess'), { variant: 'success' });
      if (isNew && saved.id) {
        allowNavigation();
        navigate(`/dashboard/system/pages/${saved.id}`, { replace: true });
      }
    } catch (e: unknown) {
      enqueueSnackbar(getAxiosErrorMessage(e, t('system.pages.saveError')), { variant: 'error' });
    } finally {
      isSavingRef.current = false;
      setSaving(false);
    }
  };

  const resolvedPreviewPath = resolveFullPathsFromSegments(pathSegments, form, publicPagesById)[
    activeLang
  ];
  const parentPreviewPath = parentPage
    ? parentPage.paths[activeLang] || parentPage.paths.en
    : null;

  const localizedInputSlotProps = {
    input: {
      endAdornment: (
        <LanguageFlagSwitcher value={activeLang} onChange={setActiveLang} />
      ),
    },
  };

  const labelsError = labelFieldError(fieldErrors, activeLang);
  const pathsError = pathFieldError(fieldErrors, activeLang);

  return (
    <Box sx={dashboardPageRootSx}>
      <DynamicBreadcrumbs />
      <Box sx={dashboardPageHeaderSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            ...dashboardPageHeaderTitleSlotSx,
          }}
        >
          <LightTooltip title={t('system.pages.backToList')} placement="bottom">
            <IconButton
              onClick={handleCancel}
              aria-label={t('system.pages.backToList')}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </LightTooltip>
          <DisplayHeadingWithRule
            variant="h2"
            component="h2"
            sx={{ flex: 1, minWidth: 0 }}
            typographySx={dashboardPageHeaderTitleTypographySx}
          >
            {pageDisplayTitle}
          </DisplayHeadingWithRule>
        </Box>
        <DashboardCollapsibleHeaderActions ref={inlineActionsRef} ariaLabel={t('dashboard.pageActions')}>
          <Button variant="outlined" onClick={handleCancel} disabled={saving}>
            {t('base.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {t('form.save')}
          </Button>
        </DashboardCollapsibleHeaderActions>
      </Box>
      <PageBox
        isLoading={isLoading}
        fill
        scrollable
        sx={{ ...ticketCardGridContainerPaddingSx, pb: 2 }}
      >
        <Paper elevation={6} sx={formPaperSx}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                fullWidth
                required
                label={t('system.pages.key')}
                value={form.key}
                onChange={e => update({ key: e.target.value })}
                margin="normal"
                error={Boolean(fieldErrors.key)}
                helperText={fieldErrors.key}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }}>
              <IconPickerField
                required
                value={form.menu.icon}
                onChange={icon => update({ menu: { ...form.menu, icon } })}
                error={Boolean(fieldErrors['menu.icon'])}
                helperText={fieldErrors['menu.icon']}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.published}
                    onChange={e => update({ published: e.target.checked })}
                  />
                }
                label={t('system.pages.published')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('system.pages.kind')}</InputLabel>
                <Select
                  label={t('system.pages.kind')}
                  value={form.kind}
                  onChange={e => {
                    const kind = e.target.value as IPublicPage['kind'];
                    update({
                      kind,
                      ...(kind === 'page' ? { redirectTo: undefined } : {}),
                    });
                    if (kind === 'page') clearFieldError('redirectTo');
                  }}
                >
                  <MenuItem value="group">{t('system.pages.group')}</MenuItem>
                  <MenuItem value="page">{t('system.pages.cmsPage')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.kind === 'page' ? (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('system.pages.shell')}</InputLabel>
                  <Select
                    label={t('system.pages.shell')}
                    value={form.shell ?? 'default'}
                    onChange={e => update({ shell: e.target.value as PublicPageShell })}
                  >
                    <MenuItem value="default">{t('system.pages.shells.default')}</MenuItem>
                    <MenuItem value="home">{t('system.pages.shells.home')}</MenuItem>
                    <MenuItem value="signup">{t('system.pages.shells.signup')}</MenuItem>
                    <MenuItem value="trails">{t('system.pages.shells.trails')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            {form.kind === 'group' ? (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={Boolean(fieldErrors.redirectTo)}
                >
                  <InputLabel>{t('system.pages.redirectTo')}</InputLabel>
                  <Select
                    label={t('system.pages.redirectTo')}
                    value={redirectToValue}
                    onChange={e => update({ redirectTo: e.target.value || undefined })}
                  >
                    {redirectChildPages.map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        {getPublicPageMenuLabel(p, activeLang)}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.redirectTo ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {fieldErrors.redirectTo}
                    </Typography>
                  ) : null}
                </FormControl>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <MenuLocationsSelect
                value={normalizeMenuLocations(form.menu)}
                onChange={updateMenuLocations}
              />
            </Grid>
            {showTopMenuFields ? (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('system.pages.parentMenu')}</InputLabel>
                  <Select
                    label={t('system.pages.parentMenu')}
                    value={form.menu.parentId ?? ''}
                    onChange={e =>
                      update({ menu: { ...form.menu, parentId: e.target.value || null } })
                    }
                  >
                    <MenuItem value="">-</MenuItem>
                    {groups.map(g => (
                      <MenuItem key={g.id} value={g.id}>
                        {getPublicPageMenuLabel(g, activeLang)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                required
                label={t('system.pages.labels')}
                value={form.menu.labels[activeLang]}
                onChange={e => updateLabel(activeLang, e.target.value)}
                margin="normal"
                error={Boolean(labelsError)}
                helperText={labelsError}
                slotProps={localizedInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                required
                label={t('system.pages.paths')}
                value={pathSegments[activeLang]}
                onChange={e => updatePathSegment(activeLang, e.target.value)}
                margin="normal"
                error={Boolean(pathsError)}
                slotProps={localizedInputSlotProps}
                helperText={
                  pathsError ?? (
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                        {parentPage
                          ? t('system.pages.pathSegmentNested', {
                            parent: parentPreviewPath,
                            full: resolvedPreviewPath,
                            interpolation: { escapeValue: false },
                          })
                          : t('system.pages.pathSegmentRootHint', {
                            full: resolvedPreviewPath,
                            interpolation: { escapeValue: false },
                          })}
                      </Box>
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => handleSlugify(activeLang)}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap', verticalAlign: 'top' }}
                      >
                        {t('system.pages.slugify')}
                      </Link>
                    </Box>
                  )
                }
              />
            </Grid>
          </Grid>

        </Paper>

        {isPageKind && isHomeShell ? (
          <HomeHeroSectionEditor
            document={blocksEditor.document}
            onDocumentChange={blocksEditor.setDocument}
            selectedEditorLang={blocksEditor.selectedEditorLang}
            setSelectedEditorLang={blocksEditor.setSelectedEditorLang}
            contentKey={effectiveContentKey}
            pageContentId={blocksEditor.pageContent?.id}
            trackMaps={trackMaps}
            onMoveBlock={blocksEditor.moveBlock}
            onMoveByDrag={blocksEditor.moveBlockByDrag}
            onUpdate={blocksEditor.updateBlock}
            onRemove={blocksEditor.removeBlock}
            onAddAt={(zone, type, columnCount, insertIndex) =>
              blocksEditor.addBlockAt(
                { kind: 'zone', zone, insertIndex },
                type,
                columnCount ? { columnCount } : undefined,
              )
            }
          />
        ) : null}

        {isPageKind && isSignupShell ? (
          <SignupPageBlocksPanel
            document={blocksEditor.document}
            pageContent={blocksEditor.pageContent}
            onDocumentChange={blocksEditor.setDocument}
          />
        ) : null}

        {isPageKind ? (
          <PageDocumentEditor
            document={blocksEditor.document}
            onDocumentChange={blocksEditor.setDocument}
            contentKey={effectiveContentKey}
            pageContentId={blocksEditor.pageContent?.id}
            selectedEditorLang={blocksEditor.selectedEditorLang}
            setSelectedEditorLang={blocksEditor.setSelectedEditorLang}
            trackMaps={trackMaps}
            showHeroZone={false}
            showBodyZone
            isSignupShell={isSignupShell}
            onMoveBlock={blocksEditor.moveBlock}
            onMoveByDrag={blocksEditor.moveBlockByDrag}
            onUpdate={blocksEditor.updateBlock}
            onRemove={blocksEditor.removeBlock}
            onAddAt={(zone, type, columnCount, insertIndex) =>
              blocksEditor.addBlockAt(
                { kind: 'zone', zone, insertIndex },
                type,
                columnCount ? { columnCount } : undefined,
              )
            }
          />
        ) : null}

        {!inlineActionsVisible ? (
          <>
            <Box sx={{ height: 88 }} aria-hidden />
            <PageEditorActionFab
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
            />
          </>
        ) : null}
      </PageBox>
    </Box>
  );
}
